import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BasketHeader } from './BasketHeader';
import { BasketMenu } from './BasketMenu';
import { TopicItem } from './TopicItem';
import { ChevronDown } from 'lucide-react';
import StudyModeBasketCard from './StudyModeBasketCard';
import BasketStats from './BasketStats';

const highlightText = (text, searchTerm) => {
  if (!searchTerm) return text;
  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return parts.map((part, index) => 
    part.toLowerCase() === searchTerm.toLowerCase() ? 
      <span key={index} className="bg-yellow-200">{part}</span> : part
  );
};

const useNewBasketIndicator = (basketId) => {
  const [showNewIndicator, setShowNewIndicator] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.shouldScrollToBasket === basketId) {
      const timer = setTimeout(() => {
        setShowNewIndicator(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [basketId, location.state?.shouldScrollToBasket]);

  return location.state?.shouldScrollToBasket === basketId && showNewIndicator;
};

export const BasketCard = ({ 
  basket, 
  onClick, 
  onMenuClick,
  onAddClick,
  searchTerm, 
  isSelected, 
  isClickable,
  isBSIComponent = false,
  workspaceId,
  workspaceName,
  studyMode,
  onRevealBasket,
  isRevealed,
  hiddenTopics = [],
  onRevealTopic,
}) => {
  const showNewIndicator = useNewBasketIndicator(basket.id);
  const location = useLocation();
  const topics = basket.topics || [];
  const visibleTopics = topics.slice(0, 4);

  if (studyMode) {
    return (
      <StudyModeBasketCard
        basket={basket}
        onClick={onClick}
        onMenuClick={onMenuClick}
        onAddClick={onAddClick}
        searchTerm={searchTerm}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        onRevealBasket={onRevealBasket}
        isRevealed={isRevealed}
        hiddenTopics={hiddenTopics}
        onRevealTopic={onRevealTopic}
      />
    );
  }

  const handleClick = (e) => {
    if (!e.target.closest('.menu-button')) {
      console.log('Clicked basket:', basket, basket.id, 'workspaceName:', workspaceName);
      onClick(basket.id, basket, workspaceId, workspaceName);
    }
  };

  const handleMenuClick = (action) => (e) => {
    e.stopPropagation();
    if (onMenuClick && typeof onMenuClick === 'function') {
      onMenuClick(basket, action);
    }
  };

  // Updated card classes based on the new style guide
  const cardClasses = `
    bg-white rounded-xl p-4 pt-2 w-full 
    transition-all duration-300 relative
    ${isBSIComponent ? (
      isClickable 
        ? 'cursor-pointer hover:bg-cranberry-50' 
        : 'cursor-not-allowed opacity-50'
    ) : 'cursor-pointer hover:shadow-md'}
    ${isBSIComponent && isSelected ? 'border-2 border-cranberry-800' : 'border-2 border-neutral-900'}
    shadow-sm
    h-[320px] flex flex-col
  `;

  // Determine the layout for topics
  const topicsLayoutClass = 'grid grid-cols-2 gap-2 mt-0 flex-grow overflow-hidden';

  const renderTopicsList = () => {
    return visibleTopics.map((topic, index) => {
      const topicWithHighlightedConcepts = {
        ...topic,
        concepts: topic.concepts?.map(concept => ({
          ...concept
        })) 
      };

      // Return TopicItem directly in non-study mode
      return (
        <TopicItem 
          key={topic.id} 
          topic={topicWithHighlightedConcepts}
          searchTerm={searchTerm}
          highlightText={highlightText}
        />
      );
    });
  };

  return (
    <div 
      id={`basket-${basket.id}`}
      className={`${cardClasses} group ${
        location.state?.shouldScrollToBasket === basket.id ? 'highlight-basket' : ''
      }`}
      onClick={handleClick}
    >
      {showNewIndicator && (
        <div className="absolute -top-2.5 -right-2.5 transition-opacity duration-500">
          <div className="bg-blue-600 text-white px-2 py-0.5 rounded-md text-xs font-medium shadow-sm">
            New
          </div>
        </div>
      )}

      <div className="flex justify-between items-start relative mb-0">
        <BasketHeader 
          name={highlightText(basket.name, searchTerm)} 
          description={basket.description}
        />
        
        <div className="ml-auto">
          {topics.length > 0 && <BasketStats topics={topics} />}
        </div>
      </div>
      
      <div className={topicsLayoutClass}>
        {renderTopicsList()}
        {topics.length === 0 && (
          <div className="col-span-2 flex justify-center items-center h-full text-neutral-600 italic">
            No topics yet
          </div>
        )}
      </div>
      
      <div className="mt-auto pt-2 flex justify-between items-center border-t border-neutral-100">
        <div className="w-full flex justify-center">
          {topics.length > 4 && (
            <button
              onClick={handleClick}
              className="text-blue-600 hover:text-blue-800 transition duration-300 text-center flex items-center justify-center"
            >
              <ChevronDown size={18} className="mr-1" />
              <span className="text-sm font-medium">View All ({topics.length} topics)</span>
            </button>
          )}
        </div>

        {!isBSIComponent && (
          <div className="absolute bottom-2 right-2 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <BasketMenu
              onEdit={handleMenuClick('edit')}
              onDelete={handleMenuClick('delete')}
            />
          </div>
        )}
      </div>
    </div>
  );
};
